import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldAlert, ShieldCheck, Lock, Zap, Database, Search, Target, Server } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SubstrateHoldVerification: React.FC = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<any>(null);
  const stats = MathService.substrateHoldVerification();

  const handleVerify = async () => {
    setIsVerifying(true);
    setResult(null);
    
    // Hold verification sequence
    await new Promise(r => setTimeout(r, 2000));
    
    setResult(stats);
    setIsVerifying(false);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Substrate <span className="text-lattice-cyan italic">Hold Verification</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Strategic Hold Assessment. Verifying the integrity of the Sovereign Sink and the strength of the Truth Barrier.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
             <div className="p-6 rounded-2xl bg-white/5 border border-white/5 flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-lattice-cyan/10 flex items-center justify-center border border-lattice-cyan/20">
                   <Target className="w-6 h-6 text-lattice-cyan" />
                </div>
                <div>
                   <span className="text-[8px] font-mono text-white/30 uppercase tracking-widest block mb-1">Stability Mode</span>
                   <p className="text-sm font-mono font-bold text-white tracking-widest uppercase">INTEGER_STABLE</p>
                </div>
             </div>
             <div className="p-6 rounded-2xl bg-white/5 border border-white/5 flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-lattice-cyan/10 flex items-center justify-center border border-lattice-cyan/20">
                   <ShieldAlert className="w-6 h-6 text-lattice-cyan" />
                </div>
                <div>
                   <span className="text-[8px] font-mono text-white/30 uppercase tracking-widest block mb-1">Barrier State</span>
                   <p className="text-sm font-mono font-bold text-white tracking-widest uppercase">ACTIVE_GUARD</p>
                </div>
             </div>
          </div>

          <button 
            onClick={handleVerify}
            disabled={isVerifying}
            className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
          >
            {isVerifying ? <Search className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
            {isVerifying ? "VERIFYING STRATEGIC HOLD..." : "EXECUTE HOLD VERIFICATION"}
          </button>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black flex flex-col justify-between"
                >
                   <div>
                      <div className="flex items-center gap-3 mb-10">
                        <Lock className="w-6 h-6" />
                        <span className="text-xs font-mono font-bold uppercase tracking-widest">Hold Status: Absolute</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Substrate Status</span>
                            <p className="text-2xl font-display font-bold tracking-tight uppercase italic">{result.status}</p>
                         </div>
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Treasury Sink</span>
                            <p className="text-xs font-mono font-bold leading-tight opacity-70 mb-2">{result.sink}</p>
                            <span className="text-[10px] font-mono font-bold uppercase py-1 px-3 bg-black/10 rounded-full">{result.treasuryStatus}</span>
                         </div>
                         <div className="grid grid-cols-2 gap-4">
                            <div className="bg-black/5 p-4 rounded-xl">
                               <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Stability</span>
                               <p className="text-sm font-display font-bold tracking-tighter leading-tight uppercase font-italic">VERIFIED</p>
                            </div>
                            <div className="bg-black/5 p-4 rounded-xl">
                               <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Barrier</span>
                               <p className="text-sm font-display font-bold tracking-tighter leading-tight uppercase font-italic">{result.barrierStatus}</p>
                            </div>
                         </div>
                      </div>

                      <div className="mt-10 p-6 border-2 border-black/10 rounded-2xl bg-black/5">
                         <p className="text-[11px] font-mono font-bold leading-relaxed italic opacity-80">
                           "{result.message}"
                         </p>
                      </div>
                   </div>

                   <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
                       <Server className="w-10 h-10 opacity-20" />
                       <div className="flex flex-col items-end">
                         <span className="text-[8px] font-mono uppercase font-bold opacity-60">Architect State</span>
                         <span className="text-sm font-display font-bold tracking-widest uppercase italic">Waiting</span>
                       </div>
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <Database className="w-20 h-20 text-white/5" />
                      {isVerifying && (
                        <motion.div 
                          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity }}
                          className="absolute inset-0 bg-lattice-cyan rounded-full filter blur-3xl"
                        />
                      )}
                   </div>
                   <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed">
                     {isVerifying ? "Verifying hold integrity..." : "System Primed: Awaiting Verification Request"}
                   </p>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>

      <div className="mt-12 pt-10 border-t border-white/5 flex flex-wrap items-center justify-center gap-12">
         <div className="flex items-center gap-2">
            <Zap className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">No Lease/Lending</span>
         </div>
         <div className="flex items-center gap-2">
            <Lock className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Anchor Persistent</span>
         </div>
         <div className="flex items-center gap-2">
            <ShieldCheck className="w-3 h-3 text-white/20" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">11B Node Consensus</span>
         </div>
      </div>
    </div>
  );
};
